// ============================================================================
// BESPOKIAN Health - Pharmacist Final Check Page
// Final verification before dispensing - PHARMACIST role required
// ============================================================================

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Pill, 
  User, 
  FileText, 
  Package,
  Printer,
  Camera,
  Scale,
  Clock,
  Shield
} from 'lucide-react';
import toast from 'react-hot-toast';
import { queueApi } from '@/api/queue.api';
import { formatCurrency } from '@/types';
import { Button, Card, CardHeader, CardTitle, CardContent, Badge, Alert, Spinner } from '@/components/common/ui';
import { StatusBadge } from '@/components/common/StatusBadge';
import { ControlledSubstanceBadge, ControlledSubstanceWarning } from '@/components/common/ControlledSubstanceBadge';
import { PriorityBadge, getPrescriptionPriority } from '@/components/common/PriorityBadge';
import { useAuth } from '@/contexts/AuthContext';

// ============================================================================
// Types
// ============================================================================

interface ChecklistItem {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  required: boolean;
  icon: React.ReactNode;
}

// ============================================================================
// Component
// ============================================================================

export function PharmacistCheckPage() {
  const { id } = useParams({ strict: false });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Only pharmacists can perform final check
  const isPharmacist = user?.role === 'PHARMACIST';

  // Checklist state
  const [checklist, setChecklist] = useState<ChecklistItem[]>([
    {
      id: 'correct_drug',
      label: 'Correct Drug',
      description: 'Drug name, strength, and form match prescription',
      checked: false,
      required: true,
      icon: <Pill className="h-5 w-5" />,
    },
    {
      id: 'correct_quantity',
      label: 'Correct Quantity',
      description: 'Quantity dispensed matches prescribed amount',
      checked: false,
      required: true,
      icon: <Scale className="h-5 w-5" />,
    },
    {
      id: 'correct_patient',
      label: 'Correct Patient',
      description: 'Patient name and DOB verified on label',
      checked: false,
      required: true,
      icon: <User className="h-5 w-5" />,
    },
    {
      id: 'correct_directions',
      label: 'Correct Directions',
      description: 'Sig code and auxiliary labels are accurate',
      checked: false,
      required: true,
      icon: <FileText className="h-5 w-5" />,
    },
    {
      id: 'lot_expiration',
      label: 'Lot & Expiration',
      description: 'Lot number recorded, expiration date acceptable',
      checked: false,
      required: true,
      icon: <Clock className="h-5 w-5" />,
    },
    {
      id: 'packaging',
      label: 'Proper Packaging',
      description: 'Container type appropriate, child-resistant if required',
      checked: false,
      required: true,
      icon: <Package className="h-5 w-5" />,
    },
    {
      id: 'appearance',
      label: 'Drug Appearance',
      description: 'Pills/tablets match NDC image, no damage or discoloration',
      checked: false,
      required: true,
      icon: <Camera className="h-5 w-5" />,
    },
    {
      id: 'double_count',
      label: 'Double Count (C-II)',
      description: 'Controlled substance count verified by second person',
      checked: false,
      required: false, // Dynamically required for C-II
      icon: <Shield className="h-5 w-5" />,
    },
  ]);

  const [counselingOffered, setCounselingOffered] = useState(false);
  const [counselingDeclined, setCounselingDeclined] = useState(false);
  const [notes, setNotes] = useState('');

  // Fetch prescription details
  const { data: prescription, isLoading, error } = useQuery({
    queryKey: ['prescription', id, 'check'],
    queryFn: () => queueApi.getPrescription(id as string),
    enabled: !!id,
  });

  // Update checklist for controlled substances
  useEffect(() => {
    if (prescription?.deaSchedule === 'CII') {
      setChecklist(prev => 
        prev.map(item => 
          item.id === 'double_count' 
            ? { ...item, required: true }
            : item
        )
      );
    }
  }, [prescription?.deaSchedule]);

  // Check mutation
  const checkMutation = useMutation({
    mutationFn: (data: { prescriptionId: string; approved: boolean; notes?: string }) =>
      queueApi.pharmacistCheck(data.prescriptionId, { approved: data.approved, notes: data.notes }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['queue'] });
      queryClient.invalidateQueries({ queryKey: ['queueCounts'] });
      queryClient.invalidateQueries({ queryKey: ['prescription', id] });
      
      if (variables.approved) {
        toast.success('Prescription approved - ready for pickup');
      } else {
        toast.error('Prescription returned for correction');
      }
      
      navigate({ to: '/queue/check' });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to complete check');
    },
  });

  // Print label mutation
  const printMutation = useMutation({
    mutationFn: () => queueApi.printLabel(id as string),
    onSuccess: () => {
      toast.success('Label sent to printer');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to print label');
    },
  });

  // Toggle checklist item
  const toggleItem = (itemId: string) => {
    setChecklist(prev =>
      prev.map(item =>
        item.id === itemId ? { ...item, checked: !item.checked } : item
      )
    );
  };

  // Check if all required items are checked
  const allRequiredChecked = checklist
    .filter(item => item.required)
    .every(item => item.checked);

  // Handle approval
  const handleApprove = () => {
    if (!allRequiredChecked) {
      toast.error('Complete all required checklist items');
      return;
    }

    if (!counselingOffered) {
      toast.error('Patient counseling must be offered');
      return;
    }

    checkMutation.mutate({
      prescriptionId: id as string,
      approved: true,
      notes: notes || undefined,
    });
  };

  // Handle rejection (return to fill)
  const handleReject = () => {
    if (!notes.trim()) {
      toast.error('Please provide a reason for returning');
      return;
    }

    checkMutation.mutate({
      prescriptionId: id as string,
      approved: false,
      notes,
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" />
      </div>
    );
  }

  // Error state
  if (error || !prescription) {
    return (
      <Alert variant="danger">
        <AlertTriangle className="h-5 w-5" />
        <span>Failed to load prescription details</span>
      </Alert>
    );
  }

  // Authorization check
  if (!isPharmacist) {
    return (
      <Alert variant="danger">
        <Shield className="h-5 w-5" />
        <span>Only pharmacists can perform the final check</span>
      </Alert>
    );
  }

  const priority = getPrescriptionPriority(prescription);
  const isControlled = !!prescription.deaSchedule;
  const isScheduleII = prescription.deaSchedule === 'CII';

  return (
    <div className="space-y-6 p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pharmacist Final Check</h1>
          <p className="text-gray-600 mt-1">Rx #{prescription.rxNumber}</p>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={prescription.status} />
          <PriorityBadge priority={priority} />
          {isControlled && <ControlledSubstanceBadge schedule={prescription.deaSchedule!} />}
        </div>
      </div>

      {/* C-II Warning */}
      {isScheduleII && (
        <ControlledSubstanceWarning schedule="CII" />
      )}

      {/* Prescription Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Prescription Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-500">Patient</h4>
              <p className="text-lg font-semibold">
                {prescription.patient?.firstName || ''} {prescription.patient?.lastName || prescription.patientName}
              </p>
              <p className="text-sm text-gray-600">
                DOB: {prescription.patient?.dateOfBirth ? new Date(prescription.patient.dateOfBirth).toLocaleDateString() : 'N/A'}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Drug</h4>
              <p className="text-lg font-semibold">{prescription.drugName}</p>
              <p className="text-sm text-gray-600">
                {prescription.strength} • {prescription.quantity} {prescription.quantityUnit}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Directions</h4>
              <p className="text-gray-900">{prescription.sigCode || prescription.sig}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Prescriber</h4>
              <p className="text-gray-900">
                Dr. {prescription.prescriber?.firstName || ''} {prescription.prescriber?.lastName || prescription.prescriberName}
              </p>
              <p className="text-sm text-gray-600">
                NPI: {prescription.prescriber?.npi || 'N/A'}
              </p>
            </div>
            {prescription.fillData && (
              <>
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Lot Number</h4>
                  <p className="text-gray-900 font-mono">{prescription.fillData.lotNumber}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Expiration</h4>
                  <p className="text-gray-900">
                    {new Date(prescription.fillData.expirationDate).toLocaleDateString()}
                  </p>
                </div>
              </>
            )}
            <div>
              <h4 className="text-sm font-medium text-gray-500">Patient Copay</h4>
              <p className="text-lg font-semibold text-green-600">
                {formatCurrency(prescription.patientCopay)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fill Details */}
      {prescription.fillData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Fill Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-500">NDC:</span>
                <span className="ml-2 font-mono">{prescription.fillData.ndc}</span>
              </div>
              <div>
                <span className="text-gray-500">Filled By:</span>
                <span className="ml-2">{prescription.fillData.filledBy}</span>
              </div>
              <div>
                <span className="text-gray-500">Filled At:</span>
                <span className="ml-2">
                  {new Date(prescription.fillData.filledAt).toLocaleString()}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Verification Checklist */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            Verification Checklist
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {checklist.map(item => (
              <label
                key={item.id}
                className={`flex items-start gap-4 p-3 rounded-lg border cursor-pointer transition-colors ${
                  item.checked
                    ? 'bg-green-50 border-green-200'
                    : item.required
                    ? 'bg-white border-gray-200 hover:border-blue-300'
                    : 'bg-gray-50 border-gray-100 hover:border-gray-200'
                } ${!item.required && !isScheduleII && item.id === 'double_count' ? 'hidden' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={item.checked}
                  onChange={() => toggleItem(item.id)}
                  className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    {item.icon}
                    <span className="font-medium text-gray-900">{item.label}</span>
                    {item.required && (
                      <Badge variant="warning" className="text-xs">Required</Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-0.5">{item.description}</p>
                </div>
                {item.checked && (
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                )}
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Patient Counseling */}
      <Card>
        <CardHeader>
          <CardTitle>Patient Counseling</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={counselingOffered}
                onChange={(e) => setCounselingOffered(e.target.checked)}
                className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-gray-900">Patient counseling offered</span>
              <Badge variant="warning" className="text-xs">Required</Badge>
            </label>
            {counselingOffered && (
              <label className="flex items-center gap-3 ml-8">
                <input
                  type="checkbox"
                  checked={counselingDeclined}
                  onChange={(e) => setCounselingDeclined(e.target.checked)}
                  className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-gray-600">Patient declined counseling</span>
              </label>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      <Card>
        <CardHeader>
          <CardTitle>Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any notes about this verification..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t">
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => navigate({ to: '/queue/check' })}
          >
            Back to Queue
          </Button>
          <Button
            variant="secondary"
            onClick={() => printMutation.mutate()}
            disabled={printMutation.isPending}
          >
            <Printer className="h-4 w-4 mr-2" />
            Reprint Label
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="danger"
            onClick={handleReject}
            disabled={checkMutation.isPending}
          >
            <XCircle className="h-4 w-4 mr-2" />
            Return for Correction
          </Button>
          <Button
            variant="success"
            onClick={handleApprove}
            disabled={!allRequiredChecked || !counselingOffered || checkMutation.isPending}
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Approve & Ready
          </Button>
        </div>
      </div>
    </div>
  );
}
